import React from 'react';
import { Typography, Card, Row, Col, Tag, List, Statistic } from 'antd';
import ReactMarkdown from 'react-markdown';
import { AnalysisResult, AnalysisStatus } from '../../../types';
import './markdown.css';
import { DollarCircleOutlined, CarOutlined, AreaChartOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface MarketInsightsProps {
  results: AnalysisResult;
  status: AnalysisStatus;
}

const MarketInsights: React.FC<MarketInsightsProps> = ({ results, status }) => {
  const { market_insights } = results;

  if (status === 'error') {
    return <Text>There was an unexpected error with market insights, once the report is complete, please rerun the analysis.</Text>;
  } else if (status === 'skipped') {
    return <Text>Market insights were skipped by the user.</Text>;
  }

  const data = market_insights?.structured_data;

  if (!data) {
    const content = market_insights?.message_content || market_insights?.cleaned_content;
    return content ? (
      <div className="markdown-content" style={{ marginBottom: '64px' }}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    ) : (
      <Text type="secondary">Analysis in progress...</Text>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', paddingBottom: '64px' }}>
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Title level={2} style={{ color: '#2c3e50', borderBottom: '2px solid #4a90e2', paddingBottom: 12 }}>📈 Market Analysis</Title>

        <div style={{ textAlign: 'center', margin: '24px 0' }}>
          <Text style={{ fontSize: '1rem', color: '#595959', display: 'block' }}>Suggested Asking Price</Text>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '16px',
            borderRadius: 8,
            textAlign: 'center',
            margin: '8px 0 24px 0',
            fontSize: '2.25rem',
            fontWeight: 700,
            display: 'inline-block',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            {data.suggested_asking_price}
          </div>
          <Text style={{ fontSize: '1rem', color: '#8c8c8c', display: 'block' }}>
            Estimated Market Value: {data.market_value_estimate}
          </Text>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card><Statistic title="Avg. Sold Price" value={data.average_price_similar_sold_listings} prefix={<DollarCircleOutlined />} valueStyle={{ fontSize: '18px' }} /></Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card><Statistic title="Avg. Mileage" value={data.average_mileage_sold_listings} prefix={<CarOutlined />} valueStyle={{ fontSize: '18px' }} /></Card>
          </Col>
          {/* <Col xs={24} sm={12} lg={6}>
            <Card><Statistic title="Est. Time to Sell" value={data.estimated_time_to_sell} prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: '18px' }} /></Card>
          </Col> */}
          <Col xs={24} sm={12} lg={8}>
            <Card><Statistic title="Similar Sold Listings" value={data.number_similar_sold_listings} prefix={<AreaChartOutlined />} valueStyle={{ fontSize: '18px' }} /></Card>
          </Col>
        </Row>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Title level={3} style={{ color: '#2c3e50' }}>⚖️ Factors Impacting Value</Title>
        <Row gutter={[32, 16]}>
          <Col xs={24} md={12}>
            <Title level={4}><Tag color="success">✓ Positives</Tag></Title>
            <List
              dataSource={data.factors_impacting_value.positives}
              renderItem={(item: string) => <List.Item style={{ borderLeft: '4px solid #27ae60', padding: '12px 16px', background: '#f6ffed', marginBottom: 8, borderRadius: 4 }}>{item}</List.Item>}
            />
          </Col>
          <Col xs={24} md={12}>
            <Title level={4}><Tag color="error">⚠ Negatives</Tag></Title>
            <List
              dataSource={data.factors_impacting_value.negatives}
              renderItem={(item: string) => <List.Item style={{ borderLeft: '4px solid #e74c3c', padding: '12px 16px', background: '#fff1f0', marginBottom: 8, borderRadius: 4 }}>{item}</List.Item>}
            />
          </Col>
        </Row>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Title level={3} style={{ color: '#2c3e50' }}>🔍 Comparable Listings Summary</Title>
        <Paragraph><strong>Sold Listings:</strong> {data.comparable_listings.sold_listings}</Paragraph>
        <Paragraph><strong>Active Listings:</strong> {data.comparable_listings.active_listings}</Paragraph>
      </Card>
    </div>
  );
};

export default MarketInsights;
